// @flow
import { TrashSimple } from '@phosphor-icons/react'
import { Button, Select as SelectAntd } from 'antd'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import type { ConditionalRule as ConditionalRuleType } from 'types/conditional-rule'
import DateField from 'ui/molecules/DateField'
import NumericText from 'ui/NumericText'

import { CONDITION_LIST } from '@/domain/constants/conditions'
import { getFilterOption } from '@/utils/getFilterOption'
import cn from '@/utils/tailwind/cn'

type Props = {
  addRule: (groupId: string) => void,
  updateRule: (
    groupId: string,
    ruleId: string,
    key: string,
    value: string
  ) => void,
  removeRule: (groupId: string, ruleId: string) => void,
  updateLogicalOperator: (
    groupId: string,
    ruleId: string,
    newLogicalOperator: 'AND' | 'OR'
  ) => void,
  isLast: boolean,
  rule: ConditionalRuleType,
  groupId: string,
  isDirty: boolean,
  totalRule: number,
  rulePosition: number,
  groupPosition: number,
}

function ConditionalRule({
  addRule,
  updateRule,
  removeRule,
  updateLogicalOperator,
  isLast,
  rule,
  groupId,
  isDirty,
  totalRule,
  rulePosition,
  groupPosition,
}: Props): React.Node {
  const { orderAllQuestions } = React.useContext(FormBuilderContext)
  const questions = orderAllQuestions()

  const filteredQuestions = React.useMemo(() => {
    let _questions = Object.keys(questions).filter(
      (key) => rule?.forQuestion !== questions[key]?.id
    )
    return _questions.filter(
      (key) =>
        ['signature', 'radio', 'checkbox', 'datetime', 'number'].includes(
          questions[key]?.question_type?.question_type
        ) ||
        [
          'full_name',
          'name',
          'phone',
          'email',
          'single_line',
          'multi_line',
          'calculation',
        ].includes(questions[key]?.question_type?.question_subtype)
    )
  }, [questions, rule?.forQuestion])

  const choices = React.useMemo(() => {
    const question = questions?.find(
      (q) => q.id.toString() === rule?.question?.toString()
    )

    if (question?.question_type?.question_type === 'datetime') {
      return questions
        .filter(
          (q) =>
            q.id !== question.id &&
            q.question_type.question_subtype ===
              question.question_type.question_subtype
        )
        .map((q) => ({
          label: q.text,
          value: q.id.toString(),
        }))
    }

    const choices =
      question?.question_choices?.map((choice) => ({
        label: choice.text_value,
        value: choice.id.toString(),
      })) ?? []

    return choices
  }, [questions, rule])

  const shouldShowSelectField = React.useMemo(() => {
    const question = questions?.find(
      (q) => q.id === (rule?.question ?? '').toString()
    )

    return question?.question_type?.question_type === 'checkbox'
  }, [questions, rule?.question])

  const conditions = React.useMemo(() => {
    const selectedQuestion = questions?.find(
      (q) => q.id === (rule.question ?? '').toString()
    )

    if (
      [
        'full_name',
        'name',
        'phone',
        'email',
        'single_line',
        'multi_line',
        'datetime',
        'time_only',
      ].includes(selectedQuestion?.question_type?.question_subtype) ||
      ['signature'].includes(selectedQuestion?.question_type?.question_type)
    ) {
      return CONDITION_LIST?.filter?.(
        (condition) =>
          condition.value === 'IS_FILLED' || condition.value === 'IS_EMPTY'
      )
    }

    if (
      ['date_only', 'month_year'].includes(
        selectedQuestion?.question_type?.question_subtype
      )
    ) {
      return CONDITION_LIST?.filter?.((condition) =>
        ['IS_FILLED', 'IS_EMPTY', 'IS_BEFORE', 'IS_AFTER'].includes(
          condition.value
        )
      )
    }

    if (
      selectedQuestion?.question_type?.question_type === 'number' ||
      selectedQuestion?.question_type?.question_subtype === 'calculation'
    ) {
      return CONDITION_LIST?.filter?.((condition) =>
        [
          'EQUALS',
          'NOT_EQUALS',
          'GREATER_THAN',
          'LESS_THAN',
          'IS_FILLED',
          'IS_EMPTY',
        ].includes(condition.value)
      )
    }

    if (selectedQuestion?.question_type?.question_type === 'checkbox') {
      return CONDITION_LIST?.filter?.((condition) => {
        if (rule?.expected_answer?.includes(',')) {
          return [
            'EQUALS',
            'NOT_EQUALS',
            'IS_FILLED',
            'IS_EMPTY',
            'CONTAINS_ANY',
            'NOT_CONTAINS_ANY',
          ].includes(condition.value)
        }

        return [
          'EQUALS',
          'NOT_EQUALS',
          'IS_FILLED',
          'IS_EMPTY',
          'CONTAINS',
          'NOT_CONTAINS',
        ].includes(condition.value)
      })
    }

    if (selectedQuestion?.question_type?.question_type === 'radio') {
      return CONDITION_LIST?.filter?.((condition) =>
        ['EQUALS', 'NOT_EQUALS', 'IS_FILLED', 'IS_EMPTY'].includes(
          condition.value
        )
      )
    }

    return []
  }, [questions, rule?.expected_answer, rule.question])

  const disabledExpectedAnswer = React.useMemo(() => {
    return ['IS_FILLED', 'IS_EMPTY'].includes(rule.operator)
  }, [rule.operator])

  const renderFields = React.useCallback(() => {
    if (disabledExpectedAnswer) {
      return <i />
    }

    const question = questions?.find(
      (q) => q.id === (rule?.question ?? '').toString()
    )

    if (question?.question_type?.question_type === 'datetime') {
      return (
        <DateField
          errorLabel="This is required"
          hasError={isDirty && !rule.expected_answer}
          onChange={(values) =>
            updateRule(
              groupId,
              rule.id,
              'expected_answer',
              values ? values[1] : ''
            )
          }
          picker={
            question?.question_type?.question_subtype === 'month_year'
              ? 'month'
              : undefined
          }
          placeholder="Expected Answer"
          value={rule.expected_answer}
        />
      )
    }

    if (
      question?.question_type?.question_type === 'number' ||
      question?.question_type?.question_subtype === 'calculation'
    ) {
      return (
        <NumericText
          errorLabel="This is required"
          hasError={isDirty && !rule.expected_answer}
          onChange={(value) =>
            updateRule(groupId, rule.id, 'expected_answer', value)
          }
          placeholder="Expected Answer"
          type="number"
          value={rule.expected_answer?.toString()}
        />
      )
    }

    if (shouldShowSelectField) {
      return (
        <SelectAntd
          showSearch
          filterOption={(input, option) => getFilterOption(input, option)}
          hasError={false}
          mode="multiple"
          onChange={(values) => {
            const value = values ? values.join() : ''
            updateRule(groupId, rule.id, 'expected_answer', value)
          }}
          options={choices}
          value={choices.filter((choice) =>
            (rule?.expected_answer ?? '').split(',').includes(choice.value)
          )}
        />
      )
    } else {
      return (
        <SelectAntd
          showSearch
          errorLabel="This is required"
          filterOption={(input, option) => getFilterOption(input, option)}
          hasError={isDirty && !rule.expected_answer}
          onChange={(value) =>
            updateRule(groupId, rule.id, 'expected_answer', value)
          }
          options={choices}
          placeholder="Expected Answer"
          value={rule.expected_answer?.toString()}
        />
      )
    }
  }, [
    choices,
    disabledExpectedAnswer,
    groupId,
    isDirty,
    questions,
    rule.expected_answer,
    rule.id,
    rule?.question,
    shouldShowSelectField,
    updateRule,
  ])

  const questionChoices = React.useMemo(() => {
    return filteredQuestions.map((key, index) => ({
      label: `${index + 1}. ${questions[key].text || 'Untitled'}`,
      value: questions[key].id,
    }))
  }, [filteredQuestions, questions])

  return (
    <div className="mb-4 flex w-full items-center">
      <div className="grid w-full grid-cols-[72px_minmax(0,1fr)_145px_minmax(0,1fr)_50px] gap-x-3">
        {groupPosition === 0 && rulePosition === 0 ? (
          <span className="flex h-full w-full items-center justify-center px-1 text-xs font-bold text-gray-600">
            IF
          </span>
        ) : (
          <div
            className={cn(
              "relative h-full w-full before:absolute before:-bottom-2/3 before:left-1/2 before:h-7 before:w-0.5 before:rounded-xl before:bg-gray-300 before:content-['']",
              isLast && 'before:hidden',
              rulePosition === 0 && 'before:left-3/5'
            )}
          >
            <SelectAntd
              className="w-full"
              menuPosition="fixed"
              onChange={(option) =>
                updateLogicalOperator(groupId, rule.id, option)
              }
              options={['AND', 'OR'].map((value) => ({ label: value, value }))}
              placeholder="Type"
              value={rulePosition === 0 ? 'OR' : 'AND'}
            />
          </div>
        )}
        <SelectAntd
          showSearch
          errorLabel="This is required"
          filterOption={(input, option) => getFilterOption(input, option)}
          hasError={isDirty && !rule.question}
          onChange={(value) => updateRule(groupId, rule.id, 'question', value)}
          options={questionChoices}
          placeholder="Question"
          value={questionChoices?.find(
            (choice) => choice.value.toString() === rule.question?.toString()
          )}
        />
        <SelectAntd
          required
          errorLabel="This is required"
          hasError={isDirty && !rule.operator}
          onChange={(value) => updateRule(groupId, rule.id, 'operator', value)}
          options={conditions}
          placeholder="Condition"
          value={rule.operator?.toString()}
        />
        {renderFields()}
        <Button
          className="h-full w-full pl-1 pr-2 focus:!outline-none focus-visible:!outline-none active:outline-none"
          onClick={() => removeRule(groupId, rule.id)}
          type="link"
        >
          <TrashSimple className="text-2xl text-gray-800 hover:text-primary" />
        </Button>
      </div>
    </div>
  )
}

export default ConditionalRule
